import { useMemo, useRef, useState } from 'react';
import { POPULAR_TOOLS, WIZARD_TOOL_GROUPS } from '../constants';
import { CloseIcon, SearchIcon } from '../../../components/icons';
import { haptic } from '../../../utils/haptic';

const BADGE_STYLE: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(120,80,255,0.12)',
  color: 'var(--accent2, #c084fc)',
  fontSize: '0.8125rem',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'border-color 0.2s, background 0.2s',
};

const BADGE_ACTIVE: React.CSSProperties = {
  borderColor: 'rgba(180,100,255,0.35)',
  background: 'rgba(120,80,255,0.2)',
};

const SEARCH_WRAP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 8,
  padding: '8px 10px',
  marginBottom: 10,
};

const GROUP_BTN_BASE: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'all 0.15s',
};

export function ToolSelector({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (tools: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const customInputRef = useRef<HTMLInputElement>(null);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      if (activeGroup) {
        const group = WIZARD_TOOL_GROUPS.find((g) => g.name === activeGroup);
        return group ? [group] : [];
      }
      return [{ name: '★ Популярные', tools: POPULAR_TOOLS }];
    }
    return WIZARD_TOOL_GROUPS.map((g) => ({
      ...g,
      tools: g.tools.filter((t) => t.toLowerCase().includes(q)),
    })).filter((g) => g.tools.length > 0);
  }, [search, activeGroup]);

  const toggle = (tool: string) => {
    haptic.select();
    if (selected.includes(tool)) {
      onChange(selected.filter((t) => t !== tool));
    } else {
      onChange([...selected, tool]);
    }
  };

  const remove = (tool: string) => {
    haptic.select();
    onChange(selected.filter((t) => t !== tool));
  };

  const addCustom = (raw: string) => {
    const tool = raw.trim();
    if (tool && !selected.includes(tool)) {
      haptic.select();
      onChange([...selected, tool]);
    }
  };

  return (
    <div>
      {/* Поиск */}
      <div style={SEARCH_WRAP}>
        <SearchIcon size={16} style={{ opacity: 0.8 }} />
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setActiveGroup(null); }}
          placeholder="Поиск инструментов..."
          style={{
            flex: 1,
            background: 'none',
            border: 'none',
            color: '#f4f0ff',
            fontSize: '0.9375rem',
            outline: 'none',
          }}
        />
      </div>

      {/* Группы — горизонтальный скролл */}
      {!search && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 10, paddingBottom: 4 }}>
          <button
            type="button"
            style={{
              ...GROUP_BTN_BASE,
              background: !activeGroup ? 'rgba(120,80,255,0.2)' : 'rgba(255,255,255,0.06)',
              border: !activeGroup ? '1px solid rgba(120,80,255,0.4)' : '1px solid rgba(255,255,255,0.1)',
              color: !activeGroup ? '#c084fc' : 'rgba(255,255,255,0.6)',
            }}
            onClick={() => { haptic.select(); setActiveGroup(null); }}
          >
            ★ Популярные
          </button>
          {WIZARD_TOOL_GROUPS.map((group) => (
            <button
              key={group.name}
              type="button"
              style={{
                ...GROUP_BTN_BASE,
                background: activeGroup === group.name ? 'rgba(120,80,255,0.2)' : 'rgba(255,255,255,0.06)',
                border: activeGroup === group.name ? '1px solid rgba(120,80,255,0.4)' : '1px solid rgba(255,255,255,0.1)',
                color: activeGroup === group.name ? '#c084fc' : 'rgba(255,255,255,0.6)',
              }}
              onClick={() => { haptic.select(); setActiveGroup(group.name === activeGroup ? null : group.name); }}
            >
              {group.name}
            </button>
          ))}
        </div>
      )}

      {/* Чипы инструментов */}
      <div style={{ maxHeight: 160, overflowY: 'auto' }}>
        {filteredGroups.map((group) => (
          <div key={group.name} style={{ marginBottom: 12 }}>
            {(search || activeGroup) && (
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>
                {group.name}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {group.tools.map((tool) => (
                <button
                  key={tool}
                  type="button"
                  style={{
                    ...BADGE_STYLE,
                    ...(selected.includes(tool) ? BADGE_ACTIVE : {}),
                  }}
                  onClick={() => toggle(tool)}
                >
                  {tool}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Кастомный ввод */}
      <div style={{ marginTop: 8 }}>
        <input
          ref={customInputRef}
          type="text"
          placeholder="Добавить свой инструмент (Enter)"
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 8,
            padding: '7px 10px',
            color: '#f4f0ff',
            fontSize: '0.875rem',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom(e.currentTarget.value);
              e.currentTarget.value = '';
            }
          }}
          onFocus={(e) => (e.target.style.borderColor = 'rgba(180,100,255,0.35)')}
          onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.07)')}
        />
      </div>

      {/* Выбранные */}
      {selected.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>
            Выбрано ({selected.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {selected.map((tool) => (
              <span
                key={tool}
                style={{
                  ...BADGE_STYLE,
                  ...BADGE_ACTIVE,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {tool}
                <button
                  type="button"
                  onClick={() => remove(tool)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '0.9rem',
                    lineHeight: 1,
                  }}
                  aria-label="Удалить"
                >
                  <CloseIcon size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
